import React, { useState, useEffect, useCallback } from 'react';
import { Mail, MessageSquare, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Clock } from 'lucide-react';

/**
 * Compact "Recent comms" timeline for an appraisal. Reads from
 * /api/communications-log?appraisal_id=... and renders each entry as a
 * collapsible row showing who/when/what was sent.
 *
 * Hidden entirely when there are no log entries — keeps the Overview tab
 * tidy for new appraisals that have never had a message sent.
 */
export default function CommunicationsLogPanel({ api, appraisalId }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/communications-log', { params: { appraisal_id: appraisalId, limit: 20 } });
      setLog(Array.isArray(data) ? data : []);
    } catch { setLog([]); }
    setLoading(false);
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (log.length === 0) return null;

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const statusBadge = (s) => {
    if (s === 'sent') return { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Sent' };
    if (s === 'pending_integration') return { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle, label: 'Pending integration' };
    return { cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock, label: 'Draft' };
  };

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' }) + ' · ' + dt.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
    } catch { return String(d).slice(0, 16); }
  };

  return (
    <div data-testid="comms-log-panel" className="bg-white border border-[#E2E2DF] rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <MessageSquare size={14} className="text-[#D35400]" />
          Recent Communications
          <span className="text-[10px] font-mono-num text-slate-400 font-normal">({log.length})</span>
        </h3>
      </div>
      <div className="space-y-1.5">
        {log.map(entry => {
          const sb = statusBadge(entry.status);
          const Icon = entry.channel === 'email' ? Mail : MessageSquare;
          const StatusIcon = sb.icon;
          const isOpen = !!expanded[entry._id];
          return (
            <div key={entry._id} data-testid={`comms-log-entry-${entry._id}`} className="border border-[#E2E2DF] rounded-md overflow-hidden">
              <button
                onClick={() => toggle(entry._id)}
                data-testid={`comms-log-toggle-${entry._id}`}
                className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
              >
                <Icon size={14} className="text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800 truncate">{entry.script_name || '(unnamed)'}</span>
                    <span className="text-[10px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{entry.recipient}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    To <span className="font-mono-num text-slate-700">{entry.recipient_to || '—'}</span>
                    <span className="mx-1.5 text-slate-300">·</span>
                    {fmtDate(entry.created_at)}
                  </p>
                </div>
                <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] px-2 py-1 rounded-full border ${sb.cls}`}>
                  <StatusIcon size={11} />
                  {sb.label}
                </span>
                {isOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 bg-slate-50/60 border-t border-[#E2E2DF]">
                  {entry.subject && (
                    <p className="text-xs font-semibold text-slate-700 mb-1.5">Subject: {entry.subject}</p>
                  )}
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">{entry.body || '(no body)'}</pre>
                  {entry.note && <p className="text-[11px] text-amber-700 mt-2 italic">{entry.note}</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
